import {
  Purchases as NativePurchases,
  STORE_REPLACEMENT_MODE,
  PURCHASES_ERROR_CODE,
  type PurchasesError as NativePurchasesError,
  type PurchasesPackage as NativePackage,
  type StoreProductChangeInfo,
} from '@revenuecat/purchases-capacitor';
import { fromNativeCustomerInfo } from './map-native';
import { isAndroid, isNative } from './platform';
import type { AppCustomerInfo, AppPackage } from './types';

export class PurchaseCancelledError extends Error {
  constructor() {
    super('Purchase was cancelled by the user');
    this.name = 'PurchaseCancelledError';
  }
}

export class PurchasePendingError extends Error {
  constructor() {
    super('Purchase is awaiting approval or payment');
    this.name = 'PurchasePendingError';
  }
}

export class PurchaseOwnershipError extends Error {
  constructor() {
    super('This store purchase is linked to another app account');
    this.name = 'PurchaseOwnershipError';
  }
}

function throwPurchaseError(error: unknown): never {
  const purchaseError = error as NativePurchasesError | null;
  const code = String(purchaseError?.code);
  if (
    code === PURCHASES_ERROR_CODE.RECEIPT_ALREADY_IN_USE_ERROR ||
    code === PURCHASES_ERROR_CODE.RECEIPT_IN_USE_BY_OTHER_SUBSCRIBER_ERROR
  ) {
    throw new PurchaseOwnershipError();
  }
  if (code === PURCHASES_ERROR_CODE.PAYMENT_PENDING_ERROR) {
    throw new PurchasePendingError();
  }
  if (purchaseError?.userCancelled) throw new PurchaseCancelledError();
  throw error;
}

/** Not a failure: Play accepted the DEFERRED change; callers show "change scheduled". */
export class DeferredChangeScheduledError extends Error {
  constructor() {
    super('Plan change scheduled for the end of the current billing period');
    this.name = 'DeferredChangeScheduledError';
  }
}

/** Play rejects 'charge-prorated' on a billing-period change, so use 'charge-now' there; Play-only, with oldProductIdentifier. */
export type ChangeBilling = 'store-default' | 'charge-prorated' | 'charge-now' | 'defer';

// Plan changes buy the base plan: the default option can start a per-product free trial, which only first purchases may get.
function purchasePlay(pkg: AppPackage, storeProductChangeInfo?: StoreProductChangeInfo) {
  const aPackage = pkg._sdk as NativePackage;
  const basePlan = storeProductChangeInfo
    ? aPackage.product.subscriptionOptions?.find((o) => o.isBasePlan)
    : undefined;
  if (storeProductChangeInfo && !basePlan) {
    console.warn('[billing] no base-plan option on a plan change — buying the default option');
  }
  return basePlan
    ? NativePurchases.purchaseSubscriptionOption({
        subscriptionOption: basePlan,
        storeProductChangeInfo,
      })
    : NativePurchases.purchasePackage({
        aPackage,
        ...(storeProductChangeInfo ? { storeProductChangeInfo } : {}),
      });
}

// Backstop only (the listener is the real signal); must outlast an open purchase sheet.
const DEFERRED_CONFIRM_TIMEOUT_MS = 60_000;

// With DEFERRED, RevenueCat's purchase promise never settles (upstream SDK issue), so the customer-info listener signals completion.
async function purchaseDeferredNative(
  pkg: AppPackage,
  oldProductIdentifier: string
): Promise<never> {
  let callbackId: string | undefined;
  let settle: (() => void) | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const confirmed = new Promise<'listener' | 'timeout'>((resolve) => {
    settle = () => resolve('listener');
    timer = setTimeout(() => resolve('timeout'), DEFERRED_CONFIRM_TIMEOUT_MS);
  });

  try {
    const registration = await NativePurchases.addCustomerInfoUpdateListener(() => settle?.());
    callbackId = registration as unknown as string;

    const attempt = purchasePlay(pkg, {
      oldProductIdentifier,
      replacementMode: STORE_REPLACEMENT_MODE.DEFERRED,
    }).then(() => 'listener' as const);

    const outcome = await Promise.race([attempt, confirmed]);
    if (outcome === 'timeout') {
      console.warn(
        '[billing] DEFERRED change not confirmed within timeout — assuming Play registered it'
      );
    }
    throw new DeferredChangeScheduledError();
  } catch (error) {
    if (error instanceof DeferredChangeScheduledError) throw error;
    return throwPurchaseError(error);
  } finally {
    if (timer) clearTimeout(timer);
    if (callbackId !== undefined) {
      await NativePurchases.removeCustomerInfoUpdateListener({
        listenerToRemove: callbackId,
      }).catch(() => {});
    }
  }
}

// On Play, a replacement without oldProductIdentifier double-subscribes; ending a trial must use 'charge-now' or the trial carries into the new plan.
export async function purchasePackage(
  pkg: AppPackage,
  oldProductIdentifier?: string,
  changeBilling: ChangeBilling = 'store-default'
): Promise<{ customerInfo: AppCustomerInfo }> {
  if (isNative()) {
    if (isAndroid() && changeBilling === 'defer' && oldProductIdentifier) {
      return purchaseDeferredNative(pkg, oldProductIdentifier);
    }
    try {
      const { customerInfo } = await (isAndroid()
        ? purchasePlay(
            pkg,
            oldProductIdentifier
              ? {
                  oldProductIdentifier,
                  ...(changeBilling === 'charge-now'
                    ? { replacementMode: STORE_REPLACEMENT_MODE.CHARGE_FULL_PRICE }
                    : changeBilling === 'charge-prorated'
                      ? { replacementMode: STORE_REPLACEMENT_MODE.CHARGE_PRORATED_PRICE }
                      : {}),
                }
              : undefined
          )
        : NativePurchases.purchasePackage({ aPackage: pkg._sdk as NativePackage }));
      return { customerInfo: fromNativeCustomerInfo(customerInfo) };
    } catch (error) {
      return throwPurchaseError(error);
    }
  }

  throw new Error('purchasePackage is native only; web uses Stripe Embedded Checkout');
}

/** Keeps the base-plan suffix (e.g. `:monthly`); use this, not currentActiveProductId, to tell billing periods apart. */
export async function currentActiveSubscriptionId(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const { customerInfo } = await NativePurchases.getCustomerInfo();
    return customerInfo.activeSubscriptions[0] ?? null;
  } catch {
    return null; // SDK unconfigured
  }
}

/** Strips the base-plan suffix because Play's replacement API wants the bare product id. */
export async function currentActiveProductId(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const { customerInfo } = await NativePurchases.getCustomerInfo();
    const active = customerInfo.activeSubscriptions[0];
    return active ? active.split(':')[0] : null;
  } catch {
    return null; // SDK unconfigured
  }
}

const EMPTY_CUSTOMER_INFO: AppCustomerInfo = {
  entitlements: { active: {} },
  activeSubscriptions: [],
  managementURL: null,
};

export async function getCustomerInfo(refresh = false): Promise<AppCustomerInfo> {
  if (!isNative()) return EMPTY_CUSTOMER_INFO;
  if (refresh) await NativePurchases.invalidateCustomerInfoCache();
  const { customerInfo } = await NativePurchases.getCustomerInfo();
  return fromNativeCustomerInfo(customerInfo);
}

/** Apple requires restore to be reachable from every paywall. */
export async function restorePurchases(): Promise<{ customerInfo: AppCustomerInfo }> {
  if (!isNative()) return { customerInfo: EMPTY_CUSTOMER_INFO };
  try {
    const { customerInfo } = await NativePurchases.restorePurchases();
    return { customerInfo: fromNativeCustomerInfo(customerInfo) };
  } catch (error) {
    return throwPurchaseError(error);
  }
}

export async function addCustomerInfoListener(
  listener: (info: AppCustomerInfo) => void
): Promise<() => Promise<void>> {
  if (!isNative()) return async () => {};
  const id = await NativePurchases.addCustomerInfoUpdateListener((info) =>
    listener(fromNativeCustomerInfo(info))
  );
  return async () => {
    await NativePurchases.removeCustomerInfoUpdateListener({ listenerToRemove: id });
  };
}
